import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import BackgroundIMG from "../../assets/team-background.jpg";

function TeamDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const [teamDetail, setTeamDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get slugified first word (first name)
  const formatFirstName = (name) => name?.split(" ")[0]?.toLowerCase();

  const onRefresh = useCallback(async () => {
    try {
      setLoading(true); // <--- Ensure loading starts
      const response = await fetch(
        "https://www.lagunacreek.io/blog/wp-json/custom/v1/acf-data/about-us"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const source = await response.json();
      const acf_fields = source?.acf_fields;
      const teamList = acf_fields?.team ?? [];

      const teamDetails = teamList.find(
        (a) => formatFirstName(a.team_member_name) === params?.team_name
      );

      if (teamDetails) {
        setTeamDetail(teamDetails);
      } else {
        console.warn("No matching team member found");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      // ✅ Always turn off loading, even on error
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [params?.team_name]);

  // Redirect to lowercase first word only
  useEffect(() => {
    const clean = params?.team_name?.toLowerCase().split(" ")[0];
    if (params?.team_name !== clean) {
      navigate(`/book/${clean}`, { replace: true });
    }
  }, [params?.team_name, navigate]);

  useEffect(() => {
    if (params?.team_name) {
      onRefresh();
    }
  }, [params?.team_name, onRefresh]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";

    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, "");

    // Remove leading "1" if it's there
    const number = cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;

    // Match the 10-digit number and format
    const match = number.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phone; // fallback if not valid
  };

  return (
    <>
      <section
        className="py-10 md:py-16 bg-cover bg-top bg-no-repeat"
        style={{
          backgroundImage: `url(${BackgroundIMG})`,
        }}
      >
        <div className="w-full max-w-[1570px] px-4 mx-auto">
          <div className="flex flex-wrap gap-y-8 -mx-4">
            {loading ? (
              // Skeleton Loader
              <>
                <div className="w-full lg:w-2/6 max-w-full px-4 animate-pulse">
                  <div className="w-full h-[377px] bg-gray-300 rounded-[16px]"></div>
                </div>
                <div className="w-full lg:w-4/6 max-w-full px-4 animate-pulse">
                  <div className="h-[40px] w-[70%] bg-gray-300 mb-4 rounded"></div>
                  <div className="h-[24px] w-[40%] bg-gray-200 mb-6 rounded"></div>
                  <div className="space-y-4 mb-10">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="h-[18px] bg-gray-200 rounded w-full"
                      ></div>
                    ))}
                  </div>
                  <div className="p-6 bg-white border border-[#E2E8F0] rounded-[12px] shadow-sm mb-8 space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="h-[20px] bg-gray-200 rounded w-[70%]"
                      ></div>
                    ))}
                  </div>
                  <div className="text-right">
                    <div className="inline-block bg-gray-300 h-[48px] w-[160px] rounded-xl"></div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-full lg:w-2/6 max-w-full px-4">
                  <img
                    src={teamDetail?.team_member_inner_image ?? ""}
                    alt={teamDetail?.team_member_name ?? "Team Member"}
                    className="rounded-[16px]"
                    height="377"
                    width="470"
                  />
                </div>
                <div className="w-full lg:w-4/6 max-w-full px-4">
                  <h2 className="text-[30px] md:text-[40px] lg:text-[52px] font-normal leading-tight text-[#5fa3b2] mb-[7px]">
                    {teamDetail?.team_member_name ?? ""}
                  </h2>
                  <p className="text-[20px] lg:text-[24px] font-semibold leading-tight text-[#333333] mb-4 lg:mb-6">
                    {teamDetail?.team_member_designation ?? ""}
                  </p>
                  {teamDetail?.team_member_detail ? (
                    teamDetail.team_member_detail
                      .split("\n")
                      .filter((line) => line.trim() !== "")
                      .map((line, index, arr) => (
                        <p
                          key={index}
                          className={`text-[18px] font-normal leading-[1.5] text-[#374151] ${
                            index === arr.length - 1
                              ? "mb-8 lg:mb-10"
                              : "mb-4 lg:mb-6"
                          }`}
                        >
                          {line}
                        </p>
                      ))
                  ) : (
                    <></>
                  )}
                  <div className="p-6 bg-white border border-[#E2E8F0] rounded-[12px] shadow-sm mb-8">
                    <div className="mb-4">
                      <Link
                        to={
                          teamDetail?.team_member_phone_number
                            ? `tel:${teamDetail.team_member_phone_number}`
                            : "#"
                        }
                        className="inline-block text-[16px] font-normal leading-[1.5] text-[#374151] hover:text-[#5fa3b2]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[31px] h-[31px] bg-[#5fa3b2] flex justify-center items-center rounded-full">
                            <svg
                              width="17"
                              height="17"
                              viewBox="0 0 17 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12.8263 8.2348L16.4995 11.908L14.6403 13.7672C14.3163 14.0899 13.9314 14.345 13.508 14.5177C13.0845 14.6904 12.631 14.7773 12.1737 14.7733C7.92416 14.7733 2.49963 9.34878 2.49963 5.09922C2.49591 4.64189 2.58291 4.18836 2.75561 3.76487C2.9283 3.34139 3.18326 2.95635 3.50573 2.63203L5.36492 0.773438L9.03814 4.44666L6.61962 6.86518C7.33862 8.57385 8.69607 9.93445 10.4031 10.6574L12.8263 8.2348Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                          {teamDetail?.team_member_phone_number
                            ? formatPhoneNumber(
                                teamDetail.team_member_phone_number
                              )
                            : ""}
                        </div>
                      </Link>
                    </div>
                    <div className="mb-4">
                      <Link
                        to={
                          teamDetail?.team_member_email
                            ? `mailto:${teamDetail.team_member_email}`
                            : "#"
                        }
                        className="inline-block text-[16px] font-normal leading-[1.5] text-[#374151] hover:text-[#5fa3b2]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[31px] h-[31px] bg-[#5fa3b2] flex justify-center items-center rounded-full">
                            <svg
                              width="18"
                              height="17"
                              viewBox="0 0 18 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.78232 9.05289C8.34888 9.05289 7.93309 8.8809 7.62599 8.57457L2.36256 3.30271C2.48373 2.99926 2.69281 2.73908 2.96288 2.55565C3.23296 2.37221 3.55167 2.27393 3.87799 2.27344H13.6866C14.013 2.27393 14.3317 2.37221 14.6017 2.55565C14.8718 2.73908 15.0809 2.99926 15.2021 3.30271L9.93865 8.57457C9.63154 8.8809 9.21576 9.05289 8.78232 9.05289ZM8.78146 10.1427C9.50396 10.1427 10.197 9.85609 10.7085 9.34574L15.3212 4.7342V14.2734H2.2417V4.7342L6.85441 9.34574C7.36594 9.85609 8.05895 10.1427 8.78146 10.1427Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                          {teamDetail?.team_member_email ?? ""}
                        </div>
                      </Link>
                    </div>
                    <div className="mb-0">
                      <Link
                        to={
                          teamDetail?.team_member_linkedin
                            ? `${teamDetail.team_member_linkedin}`
                            : "#"
                        }
                        target="_blank"
                        className="inline-block text-[16px] font-normal leading-[1.5] text-[#374151] hover:text-[#5fa3b2]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[31px] h-[31px] bg-[#5fa3b2] flex justify-center items-center rounded-full">
                            <svg
                              width="18"
                              height="17"
                              viewBox="0 0 18 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15.1262 1.51465H2.91458C2.35503 1.51465 1.90039 1.97564 1.90039 2.54155V14.7309C1.90039 15.2968 2.35503 15.7578 2.91458 15.7578H15.1262C15.6857 15.7578 16.1435 15.2968 16.1435 14.7309V2.54155C16.1435 1.97564 15.6857 1.51465 15.1262 1.51465ZM6.20513 13.7231H4.09409V6.92577H6.20831V13.7231H6.20513ZM5.14961 5.99743C4.47242 5.99743 3.92559 5.44741 3.92559 4.7734C3.92559 4.0994 4.47242 3.54938 5.14961 3.54938C5.82361 3.54938 6.37363 4.0994 6.37363 4.7734C6.37363 5.45059 5.82679 5.99743 5.14961 5.99743ZM14.1183 13.7231H12.0073V10.4166C12.0073 9.62816 11.9914 8.61397 10.9105 8.61397C9.81042 8.61397 9.64192 9.47237 9.64192 10.3594V13.7231H7.53089V6.92577H9.55608V7.85412H9.5847C9.86765 7.32 10.5576 6.75727 11.5845 6.75727C13.7209 6.75727 14.1183 8.16569 14.1183 9.99695V13.7231Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                          LinkedIn Profile
                        </div>
                      </Link>
                    </div>
                  </div>
                  <div className="text-right">
                    <Link
                      to="/about-us"
                      className="inline-block text-lg font-bold leading-6 text-white bg-[#5fa3b2] py-3 lg:py-4 px-9 rounded-xl transition-all ease-in-out duration-500 hover:bg-[#FAA42A]"
                    >
                      Back to Team
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default TeamDetails;
